/**
 * 「폼링크 생성하기」 + 「폼 바로 열기」를 한 칸으로 합친 버튼 (2026-08-28).
 *
 * 둘은 같은 기능(고객 입력 폼)의 두 갈래다 — 하나는 **링크를 만들어 고객에게 보내고**,
 * 하나는 **직원이 그 폼을 지금 열어** 대신 입력한다. 늘 쓰는 쪽(링크 발급)을 본 버튼에 두고,
 * 나머지는 오른쪽 끝의 ▾ 를 눌러 꺼낸다. 상단 띠에서 86px 을 돌려받는다.
 *
 * ⚠️ 두 갈래는 발급 방식이 다르다 — 링크는 1시간짜리 1인용(`single`), 바로 열기는 시간 제한
 *    없는 매장 직원용(`store`)이다. 라벨만 바꿔 한 버튼에 묶으면 안 되고, 각각 제 인자로
 *    FormLinkButton 을 부른다.
 */

import { useEffect, useRef, useState } from "react";

import FormLinkButton from "./FormLinkFAB";

export default function FormLinkSplitButton({ className = "" }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`relative flex shrink-0 items-center ${className}`}>
      <FormLinkButton className="h-9 rounded-l-lg rounded-r-none bg-brand-500 px-3 text-xs font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60 flex items-center gap-1.5" />
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="폼 관련 기능 더 보기"
        title="폼 관련 기능 더 보기"
        className="flex h-9 w-6 shrink-0 items-center justify-center rounded-r-lg border-l border-white/25 bg-brand-500 text-[10px] text-white transition hover:bg-brand-600"
      >
        {open ? "▲" : "▼"}
      </button>

      {open && (
        // 항목을 누르면 발급이 시작되므로(비동기) 클릭이 올라온 뒤 닫는다.
        <div
          role="menu"
          aria-label="폼 기능"
          onClick={() => setOpen(false)}
          className="absolute right-0 top-full z-99999 mt-1 w-[13rem] rounded-lg border border-gray-200 bg-white p-1 shadow-theme-lg dark:border-gray-800 dark:bg-gray-900"
        >
          <FormLinkButton
            openInNewTab
            kind="store"
            label="↗ 폼 바로 열기"
            title="로그인한 판매점 직원용 입력 페이지를 새 탭에서 바로 엽니다"
            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs font-medium text-gray-700 transition hover:bg-gray-100 disabled:opacity-60 dark:text-gray-200 dark:hover:bg-gray-800"
          />
        </div>
      )}
    </div>
  );
}
